/**
 * Pure queue logic functions shared between worker and landing page demo.
 * These functions have no side effects and can be used anywhere.
 */

/**
 * Simple queue item type for the demo (subset of full QueueItem)
 */
export interface DemoQueueItem {
  id: string;
  text: string;
  addedBy: string;
  addedByName?: string;
  addedAt?: string;
  assignedTo?: string;
  assignedToName?: string;
  done?: boolean;
  botMessageId?: string;
  assignMessageId?: string;
  sourceMessageId?: string;
  requestedBy?: string;
}

/**
 * Result of finding an item by text or index
 */
export type FindItemResult =
  | { found: true; index: number }
  | { found: false; error: "not_found" | "multiple_matches" };

/**
 * Find an item in the queue by number (1-indexed) or text match
 */
export function findItemByArg<T extends { text: string }>(queue: T[], arg: string): FindItemResult {
  if (!arg) {
    // No arg means first item
    return queue.length > 0 ? { found: true, index: 0 } : { found: false, error: "not_found" };
  }

  const num = parseInt(arg, 10);
  if (!Number.isNaN(num) && num >= 1 && num <= queue.length) {
    return { found: true, index: num - 1 };
  }

  // Text match
  const matches = queue
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => item.text.toLowerCase().includes(arg.toLowerCase()));

  if (matches.length === 0) {
    return { found: false, error: "not_found" };
  }

  if (matches.length > 1) {
    return { found: false, error: "multiple_matches" };
  }

  return { found: true, index: matches[0].i };
}

/**
 * Check if an item with the same text already exists (case-insensitive)
 */
export function isDuplicateItem<T extends { text: string }>(queue: T[], text: string): boolean {
  const textLower = text.toLowerCase();
  return queue.some((item) => item.text.toLowerCase() === textLower);
}

/**
 * Find the first unassigned item in the queue
 */
export function findFirstUnassigned<T extends { assignedTo?: string }>(queue: T[]): number {
  return queue.findIndex((item) => !item.assignedTo);
}

/**
 * Find the first item that's not done
 */
export function findFirstNotDone<T extends { done?: boolean }>(queue: T[]): number {
  return queue.findIndex((item) => !item.done);
}

/**
 * Format a queue list for display (simplified version for demo)
 */
export function formatQueueListSimple(queue: DemoQueueItem[]): string {
  if (queue.length === 0) {
    return "*Queue (0 items):*\nQueue is empty.";
  }

  const lines = queue.map((item, i) => {
    const parts = [`${i + 1}. ${item.text}`];
    parts.push(`By: ${item.addedByName || item.addedBy}`);
    if (item.assignedToName || item.assignedTo) {
      parts.push(`Assigned: ${item.assignedToName || item.assignedTo}`);
    }
    if (item.addedAt) {
      parts.push(formatTimeAgo(item.addedAt));
    }
    if (item.done) {
      parts.push("Done");
    }
    return parts.join(" | ");
  });

  return `*Queue (${queue.length} item${queue.length === 1 ? "" : "s"}):*\n${lines.join("\n")}`;
}

/**
 * Truncate text to a maximum length
 */
export function truncateText(text: string, maxLength = 100): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

/**
 * Format relative time (e.g., "5m ago", "2h ago")
 */
export function formatTimeAgo(isoString: string): string {
  const now = new Date();
  const past = new Date(isoString);
  const diffMs = now.getTime() - past.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
}
